import { useState, type DragEvent } from "react";
import { Badge, cn } from "../../../../components/ui";
import { useMxLabels } from "../../hooks/useMxLabels";
import { costOf, fmtMoney, isOverdue, photosOf, thumbOf } from "../../lib";
import { CATEGORY_ICONS, STATUS_ACTIONS, STATUS_CHART_COLORS, STATUS_ICONS, STATUS_TRANSITIONS, categoryOf } from "../../theme";
import type { MaintenanceRecord, MaintenanceStatus, VehicleOption } from "../../types";
import { PhotoGallery, VehicleThumb } from "./Media";
import { DueText, IconAction, ModeBadge, StatusBadge, TypeBadge } from "./Primitives";

type VehicleOf = (id?: string | null) => VehicleOption | null;

export interface RecordViewProps {
  rows: MaintenanceRecord[];
  vehicleOf: VehicleOf;
  onOpen: (r: MaintenanceRecord) => void;
  canManage?: boolean;
  onTransition?: (r: MaintenanceRecord, s: MaintenanceStatus) => void;
  showType?: boolean;
}

/** Tarjeta de mantenimiento con foto de la unidad, chips, vencimiento y costo. */
export function MaintenanceCard({ record: r, vehicle, onOpen, canManage, onTransition, showType = true, compact = false }: { record: MaintenanceRecord; vehicle?: VehicleOption | null; onOpen: (r: MaintenanceRecord) => void; canManage?: boolean; onTransition?: (r: MaintenanceRecord, s: MaintenanceStatus) => void; showType?: boolean; compact?: boolean }) {
  const L = useMxLabels();
  const status = r.status || "pendiente";
  const nexts = canManage && onTransition ? STATUS_TRANSITIONS[status] ?? [] : [];
  const photos = photosOf(r);
  const cat = categoryOf(r.maintenanceDescription);
  const overdue = isOverdue(r);
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(r)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(r))}
      className={cn(
        "group flex cursor-pointer flex-col overflow-hidden rounded-xl border bg-card text-left transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        overdue ? "border-error/50" : "border-line"
      )}
    >
      {!compact ? (
        <div className="relative h-32 w-full overflow-hidden bg-subtle">
          {vehicle?.image ? (
            <img src={thumbOf(vehicle.image)} alt={vehicle.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full items-center justify-center text-content-faint">
              <i className="ri-truck-line text-3xl" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute left-2 top-2"><StatusBadge record={r} size="sm" onPhoto /></div>
          {photos.length ? (
            <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white">
              <i className="ri-camera-line" />
              {photos.length}
            </span>
          ) : null}
          <div className="absolute bottom-2 left-3 right-3">
            <p className="truncate text-sm font-semibold text-white">{vehicle?.label ?? r.vehicleId}</p>
            {vehicle?.plate ? <p className="text-[10px] uppercase tracking-wide text-white/75">{vehicle.plate}</p> : null}
          </div>
        </div>
      ) : null}
      <div className="flex flex-1 flex-col gap-2 p-3">
        {compact ? (
          <div className="flex items-center gap-2">
            <VehicleThumb vehicle={vehicle} size="xs" />
            <span className="min-w-0 flex-1 truncate text-xs font-semibold text-content">{vehicle?.label ?? r.vehicleId}</span>
          </div>
        ) : null}
        <p className="line-clamp-2 text-sm font-medium text-content">{r.maintenanceDescription || "—"}</p>
        <div className="flex flex-wrap items-center gap-1">
          {compact ? <StatusBadge record={r} size="sm" /> : null}
          {showType ? <TypeBadge type={r.type} /> : null}
          <Badge tone="neutral" size="sm">
            <i className={CATEGORY_ICONS[cat]} />
            {L.category(cat)}
          </Badge>
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <DueText record={r} />
          <span className="text-xs font-semibold tabular-nums text-content">{costOf(r) ? fmtMoney(costOf(r), L.locale) : r.workshopMode === "propio" ? L.t("mantenimiento.free") : "—"}</span>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-line pt-2">
          <span className="flex min-w-0 items-center gap-1 truncate text-[11px] text-content-secondary">
            <i className={r.workshopMode === "externo" ? "ri-store-2-line" : "ri-home-gear-line"} />
            <span className="truncate">{r.workshopName || L.mode(r.workshopMode)}</span>
          </span>
          {nexts.length && compact ? (
            // Tablero: «Mover a…» accesible por teclado (alternativa a arrastrar).
            <select
              value=""
              aria-label={L.t("mantenimiento.ui.kanban.moveTo")}
              title={L.t("mantenimiento.ui.kanban.moveTo")}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                const next = e.target.value;
                if (next) onTransition?.(r, next as MaintenanceStatus);
              }}
              className="h-7 max-w-[130px] shrink-0 rounded-md border border-line bg-card px-1.5 text-[11px] text-content-secondary outline-none focus:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <option value="">{L.t("mantenimiento.ui.kanban.moveTo")}</option>
              {nexts.map((s) => (
                <option key={s} value={s}>
                  {L.transition(s)} → {L.status(s)}
                </option>
              ))}
            </select>
          ) : nexts.length ? (
            <div className="flex shrink-0 items-center">
              {nexts.map((s) => (
                <IconAction key={s} icon={STATUS_ACTIONS[s]?.icon ?? "ri-arrow-right-line"} label={L.transition(s)} className={cn("rounded-md p-1 text-sm", STATUS_ACTIONS[s]?.className)} onClick={() => onTransition?.(r, s as MaintenanceStatus)} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export function MaintenanceCardGrid({ rows, vehicleOf, onOpen, canManage, onTransition, showType }: RecordViewProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {rows.map((r) => (
        <MaintenanceCard key={r.id} record={r} vehicle={vehicleOf(r.vehicleId)} onOpen={onOpen} canManage={canManage} onTransition={onTransition} showType={showType} />
      ))}
    </div>
  );
}

/** Tabla densa con miniatura de la unidad. */
export function MaintenanceTable({ rows, vehicleOf, onOpen, showType = true }: RecordViewProps) {
  const L = useMxLabels();
  const { t } = L;
  return (
    <div className="overflow-hidden rounded-xl border border-line bg-card">
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-subtle/60 text-[11px] uppercase tracking-wide text-content-muted">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">{t("mantenimiento.fields.vehicle")}</th>
              <th className="px-3 py-2.5 text-left font-semibold">{t("mantenimiento.fields.description")}</th>
              {showType ? <th className="px-3 py-2.5 text-left font-semibold">{t("mantenimiento.fields.type")}</th> : null}
              <th className="px-3 py-2.5 text-left font-semibold">{t("mantenimiento.fields.status")}</th>
              <th className="px-3 py-2.5 text-left font-semibold">{t("mantenimiento.fields.date")}</th>
              <th className="px-3 py-2.5 text-left font-semibold">{t("mantenimiento.fields.workshop")}</th>
              <th className="px-3 py-2.5 text-right font-semibold">{t("mantenimiento.fields.cost")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => {
              const v = vehicleOf(r.vehicleId);
              const photos = photosOf(r);
              return (
                <tr key={r.id} onClick={() => onOpen(r)} className={cn("cursor-pointer transition-colors hover:bg-subtle/50", isOverdue(r) ? "bg-error/[0.04]" : "")}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      <VehicleThumb vehicle={v} size="sm" />
                      <div className="min-w-0">
                        <p className="max-w-[180px] truncate font-medium text-content">{v?.label ?? r.vehicleId}</p>
                        <p className="text-[11px] text-content-muted">{v?.plate ?? ""}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <p className="max-w-[320px] truncate text-content">{r.maintenanceDescription || "—"}</p>
                    {photos.length ? (
                      <div className="mt-1 flex items-center gap-1">
                        <PhotoGallery photos={photos} alt={r.maintenanceDescription || ""} layout="strip" max={3} />
                      </div>
                    ) : null}
                  </td>
                  {showType ? <td className="px-3 py-2"><TypeBadge type={r.type} /></td> : null}
                  <td className="px-3 py-2"><StatusBadge record={r} size="sm" /></td>
                  <td className="px-3 py-2"><DueText record={r} /></td>
                  <td className="px-3 py-2">
                    <p className="max-w-[200px] truncate text-xs text-content">{r.workshopName || "—"}</p>
                    <ModeBadge mode={r.workshopMode} />
                  </td>
                  <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums text-content">{costOf(r) ? fmtMoney(costOf(r), L.locale) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const KANBAN_COLUMNS = ["pendiente", "agendado", "en_proceso", "completado", "cancelado"] as const;

/** Tablero por estado; arrastrar una tarjeta aplica la transición si es válida. */
export function MaintenanceKanban({ rows, vehicleOf, onOpen, canManage, onTransition, onInvalidMove }: RecordViewProps & { onInvalidMove?: () => void }) {
  const L = useMxLabels();
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const dragged = rows.find((r) => r.id === dragId) || null;
  const canDrop = (col: string) => !!dragged && (STATUS_TRANSITIONS[dragged.status || "pendiente"] ?? []).includes(col);

  const onDrop = (e: DragEvent, col: string) => {
    e.preventDefault();
    setOver(null);
    const r = dragged;
    setDragId(null);
    if (!r || (r.status || "pendiente") === col) return;
    if (!canDrop(col)) return onInvalidMove?.();
    onTransition?.(r, col as MaintenanceStatus);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {KANBAN_COLUMNS.map((col) => {
        const items = rows.filter((r) => (r.status || "pendiente") === col);
        const total = items.reduce((s, r) => s + costOf(r), 0);
        return (
          <section
            key={col}
            onDragOver={(e) => {
              if (!canManage) return;
              e.preventDefault();
              setOver(col);
            }}
            onDragLeave={() => setOver((o) => (o === col ? null : o))}
            onDrop={(e) => onDrop(e, col)}
            className={cn(
              "flex w-[290px] min-w-[290px] flex-col rounded-xl border bg-surface transition-colors",
              over === col ? (canDrop(col) ? "border-success bg-success/5" : "border-error bg-error/5") : "border-line"
            )}
          >
            <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
              <span className="flex items-center gap-2 text-sm font-semibold text-content">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_CHART_COLORS[col] }} />
                <i className={cn(STATUS_ICONS[col], "text-content-muted")} />
                {L.status(col)}
              </span>
              <span className="flex items-center gap-2">
                {total ? <span className="text-[11px] tabular-nums text-content-muted">{fmtMoney(total, L.locale, true)}</span> : null}
                <span className="rounded-full bg-subtle px-2 text-xs font-semibold text-content-secondary">{items.length}</span>
              </span>
            </header>
            <div className="flex max-h-[calc(100vh-300px)] min-h-[200px] flex-col gap-2 overflow-y-auto p-2">
              {items.length === 0 ? <p className="py-8 text-center text-xs text-content-faint">{L.t("mantenimiento.ui.kanban.empty")}</p> : null}
              {items.map((r) => (
                <div
                  key={r.id}
                  draggable={!!canManage}
                  onDragStart={(e) => {
                    setDragId(r.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOver(null);
                  }}
                  className={cn(dragId === r.id ? "opacity-50" : "")}
                >
                  <MaintenanceCard record={r} vehicle={vehicleOf(r.vehicleId)} onOpen={onOpen} canManage={canManage} onTransition={onTransition} compact />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
